import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Paperclip, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import { api } from "../api/client";

const CATEGORY_LABELS = {
    order_issue: "Order issue",
    delivery_problem: "Delivery problem",
    prescription_query: "Prescription query",
    payment_refund: "Payment / refund",
    subscription: "Subscription",
    lab_test: "Lab test",
    consultation: "Doctor consultation",
    account: "Account / login",
    feedback: "Feedback",
    other: "Other",
};

const SupportNewContent = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [recentRx, setRecentRx] = useState([]);
    const [form, setForm] = useState({
        category: "order_issue",
        subject: "",
        description: "",
        link_type: "",
        link_id: "",
    });
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/support/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
        api.get("/orders").then(({ data }) => setRecentOrders((data || []).slice(0, 10))).catch(() => {});
        api.get("/prescriptions").then(({ data }) => setRecentRx((data || []).slice(0, 5))).catch(() => {});
    }, []);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Max 5 MB per file.");
            return;
        }
        try {
            setUploading(true);
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/support/upload-attachment", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setAttachments((a) => [...a, data.path]);
            toast.success("Attachment uploaded");
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeAttachment = (idx) => setAttachments((a) => a.filter((_, i) => i !== idx));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.subject.trim() || !form.description.trim()) {
            toast.error("Subject and description are required.");
            return;
        }
        try {
            setSubmitting(true);
            const linked_entities = {};
            if (form.link_type && form.link_id) linked_entities[form.link_type] = form.link_id;
            const payload = {
                category: form.category,
                subject: form.subject.trim(),
                description: form.description.trim(),
                attachments,
                linked_entities: Object.keys(linked_entities).length ? linked_entities : null,
            };
            const { data } = await api.post("/support/tickets", payload);
            toast.success(`Ticket ${data.ticket_no} created`);
            navigate(`/support/${data.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not create ticket");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-5 rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="support-new-form">
            <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Category *</label>
                <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value, link_type: "", link_id: "" }))}
                    className="input-clean mt-1.5"
                    data-testid="support-category-select"
                >
                    {categories.map((c) => (
                        <option key={c.slug} value={c.slug}>
                            {CATEGORY_LABELS[c.slug] || c.slug}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Subject *</label>
                <input
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="One-line summary"
                    maxLength={140}
                    className="input-clean mt-1.5"
                    required
                    data-testid="support-subject-input"
                />
            </div>

            <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Description *</label>
                <textarea
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    rows={6}
                    placeholder="Tell us what's happening. Include order numbers, screenshots if useful."
                    maxLength={4000}
                    className="input-clean mt-1.5 !h-auto"
                    required
                    data-testid="support-description-input"
                />
            </div>

            <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Link to (optional)</label>
                <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                    <select
                        value={form.link_type}
                        onChange={(e) => setForm((f) => ({ ...f, link_type: e.target.value, link_id: "" }))}
                        className="input-clean"
                        data-testid="support-link-type"
                    >
                        <option value="">No link</option>
                        <option value="order_id">Order</option>
                        <option value="prescription_id">Prescription</option>
                    </select>
                    {form.link_type === "order_id" && (
                        <select
                            value={form.link_id}
                            onChange={(e) => setForm((f) => ({ ...f, link_id: e.target.value }))}
                            className="input-clean"
                            data-testid="support-link-order"
                        >
                            <option value="">Select an order…</option>
                            {recentOrders.map((o) => (
                                <option key={o.id} value={o.id}>{o.order_no} — {o.status} — ₹{o.total}</option>
                            ))}
                        </select>
                    )}
                    {form.link_type === "prescription_id" && (
                        <select
                            value={form.link_id}
                            onChange={(e) => setForm((f) => ({ ...f, link_id: e.target.value }))}
                            className="input-clean"
                            data-testid="support-link-rx"
                        >
                            <option value="">Select a prescription…</option>
                            {recentRx.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.original_filename || p.id.slice(0, 8)} — {p.verification_status}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Attachments (optional, ≤5MB each)</label>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs text-brand-800" data-testid={`support-attachment-${i}`}>
                            <Paperclip className="h-3 w-3" /> {a.split("/").pop().slice(0, 16)}…
                            <button type="button" onClick={() => removeAttachment(i)} className="text-brand-600 hover:text-rose-600"><X className="h-3 w-3" /></button>
                        </span>
                    ))}
                    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed border-zinc-300 px-3 py-1.5 text-xs text-zinc-600 hover:border-brand-300 hover:text-brand-700">
                        <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={onUpload} disabled={uploading} data-testid="support-attach-input" />
                        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                        {uploading ? "Uploading…" : "Add file"}
                    </label>
                </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={() => navigate("/support")} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50" data-testid="support-cancel-btn">
                    Cancel
                </button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="support-submit-btn">
                    {submitting ? "Creating…" : "Submit ticket"}
                </button>
            </div>
        </form>
    );
};

const SupportNew = () => (
    <Layout>
        <section className="mx-auto max-w-3xl px-6 py-10 lg:px-12">
            <button
                onClick={() => window.history.back()}
                className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700"
                data-testid="support-back-btn"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
            <header className="mb-6">
                <p className="label-uppercase">Support</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Open a new ticket</h1>
            </header>
            <AuthGuard>
                <SupportNewContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default SupportNew;
